import React, { useState } from "react";
import PropTypes from "prop-types";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import "./ExtensionDialog.css";
import ExtensionPublishConfirmation from "./ExtensionPublishConfirmation";
import ExtensionUnpublishConfirmation from "./ExtensionUnpublishConfirmation";
import ExtensionDeletionConfirmation from "./ExtensionDeletionConfirmation";
import ExtensionCreation from "../../extensions/ExtensionCreation/ExtensionCreation";
import changePublicationType from "../../../../utils/ExtensionHandlingUtils/changePublicationType";
import SubmitExtensionForReview from "../../../../utils/database/SubmitExtensionForReview";
import deleteExtension from "../../../../utils/ExtensionHandlingUtils/deleteExtension";
import useUserDetails from "../../../../utils/auth/UserDetails";
import useUserRole from "../../../../utils/auth/useUserRole";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileUploadPopup from "../../fileUploadPopup/fileUploadPopup";
import {
    Dialog as MuiDialog,
    DialogTitle,
    DialogContent, Menu, Box, MenuItem, IconButton, Divider, Chip, Typography,
    Avatar
} from "@mui/material";
import { MoreVert, Edit, UploadFile, Delete, Unpublished, PublishedWithChanges } from "@mui/icons-material";

import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
function ExtensionImage({ imagePath, iconClass, className }) {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    if (!imagePath || imageError) {
        return <i className={`extension-icon-large ${iconClass || "pi pi-folder"}`} />;
    }

    return (
        <div className="extension-image-wrapper">
            {!imageLoaded && <i className={`extension-icon-large ${iconClass}`} />}
            <img
                src={imagePath}
                alt="Extension"
                className={className}
                style={{ display: imageLoaded ? "block" : "none" }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
            />
        </div>
    );
}

ExtensionImage.propTypes = {
    imagePath: PropTypes.string,
    iconClass: PropTypes.string,
    className: PropTypes.string,
};

const ExtensionDialog = ({
    extension,
    onStartChat,
    onUpdateExtension,
    onRemoveExtension,
    readOnly = false,
    setSelectedDocumentIds = [],
    featured,
}) => {
    console.log(extension.tools);
    
    // console.log(extension,
    //     onStartChat,
    //     onUpdateExtension,
    //     onRemoveExtension);

    const userDetails = useUserDetails();
    const { isAdmin } = useUserRole();
    const isOwner = userDetails && extension?.userId === userDetails?.userId;
    const [isPublishConfirmVisible, setPublishConfirmVisible] = useState(false);
    const [isUnpublishConfirmVisible, setUnpublishConfirmVisible] = useState(false);
    const [isDeletionConfirmVisible, setDeletionConfirmVisible] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [triggerCreate, setTriggerCreate] = useState(false);
    const [triggerCancel, setTriggerCancel] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    if (!extension) return null;


    const isPublic = extension.publicationType === "public";
    const approvalStatus = extension.status || "draft";
    const isPendingReview = approvalStatus === "submitted";
    const isRejected = approvalStatus === "rejected";

    const handlePublishToggle = () => {
        if (isPublic) {
            setUnpublishConfirmVisible(true);
        } else {
            setPublishConfirmVisible(true);
        }
    };

    const handleConfirmPublish = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            // Owner-initiated publishing now goes through the approval workflow.
            // The backend decides whether to mark as submitted (regular user) or
            // auto-approve (admin); we just reflect what comes back.
            const response = await SubmitExtensionForReview(extension.userId, extension.id);
            if (response.success) {
                const newStatus = response.status || "submitted";
                const updatedExtension = {
                    ...extension,
                    status: newStatus,
                    publicationType: newStatus === "approved" ? "public" : extension.publicationType,
                };
                onUpdateExtension(updatedExtension);
                if (newStatus === "approved") {
                    toast.success(`Extension "${extension.extensionName}" published successfully!`);
                } else {
                    toast.success(`Extension "${extension.extensionName}" submitted for review!`);
                }
            } else {
                setError(response.error);
                toast.error(`Failed to submit for review: ${response.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Error submitting extension for review:", err);
            setError("An unexpected error occurred while submitting for review.");
            toast.error("An unexpected error occurred while submitting for review.");
        } finally {
            setIsProcessing(false);
            setPublishConfirmVisible(false);
        }
    };

    const handleConfirmUnpublish = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            const response = await changePublicationType(extension.id, extension.userId, "private");
            if (response.success) {
                const updatedExtension = { ...extension, publicationType: "private" };
                onUpdateExtension(updatedExtension);
                toast.success(`Extension "${extension.extensionName}" unpublished successfully!`);
            } else {
                setError(response.error);
                toast.error(`Failed to unpublish extension: ${response.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Error unpublishing extension:", err);
            setError("An unexpected error occurred while unpublishing.");
            toast.error("An unexpected error occurred while unpublishing.");
        } finally {
            setIsProcessing(false);
            setUnpublishConfirmVisible(false);
        }
    };

    const handleDeleteClick = () => {
        if ( !isPublic) {


            setDeletionConfirmVisible(true);
        } else {
            toast.warn("Unable to delete: Please unpublish the extension first.");
            return;
        }
    };
    const handleViewClick = () => {
        setShowDetailsDialog(true);
    };

    const handleUploadFile = () => {
        setShowSidebar(true);
    }

    // const handleUploadClick = () => {
    //     setShowSidebar(true);
    // }

    const handleFileChangeFromSidebar = (newFile) => {
        setSelectedFiles((prevFiles) => {
            const isFileAlreadySelected = prevFiles.some(
                (file) => file.name === newFile.name
            );
            if (isFileAlreadySelected) {
                console.warn(`File "${newFile.name}" is already selected.`);
                return prevFiles;
            }
            if (prevFiles.length >= 10) {
                console.warn("Maximum file limit (10 files) reached.");
                return prevFiles;
            }
            return [...prevFiles, newFile];
        });
    };

    const handleConfirmDelete = async () => {
        setIsProcessing(true);
        setError(null);
        try {
            const response = await deleteExtension(extension.userId, extension.id, true);
            if (response.success) {
                onRemoveExtension(extension.id);
                setDeletionConfirmVisible(false);
                toast.success(`Extension "${extension.extensionName}" deleted successfully!`);
            } else {
                setError(response.error);
                toast.error(`Failed to delete extension: ${response.error || "Unknown error"}`);
            }
        } catch (err) {
            console.error("Error deleting extension:", err);
            setError("An unexpected error occurred while deleting.");
            toast.error("An unexpected error occurred while deleting.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelDelete = () => {
        setDeletionConfirmVisible(false);
    };

    const handleEditExtension = () => {
        setShowEditDialog(true);
    };


    const handleCloseEditDialog = () => {
        setShowEditDialog(false);
    };

    const handleExtensionUpdated = (updatedExt) => {
        onUpdateExtension(updatedExt);
        setShowEditDialog(false);
    };



    return (
        <Box sx={{ display: 'flex', position: 'absolute', top: 6, right: 0 }}>

            <IconButton
                aria-label="Extension Actions"
                onClick={handleClick}
                sx={{ color: "#99cc33" }}
            >
                <MoreVert fontSize="small" />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                    "& .MuiPaper-root": {
                        minWidth: "160px",
                        backgroundColor: "white",
                        border: "1px solid #99cc33",
                        boxShadow: "none",
                    }
                }}
            >
                <MenuItem
                    onClick={() => { handleViewClick(); handleClose(); }}
                    disabled={isProcessing}
                >
                    <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} /> View Details
                </MenuItem>

                {isOwner && (
                    <>
                        <MenuItem
                            onClick={() => { handleEditExtension(); handleClose(); }}
                            disabled={isProcessing || isPendingReview}
                        >
                            <Edit fontSize="small" sx={{ mr: 1 }} />
                            {isPendingReview ? "Locked (Under Review)" : "Edit Extension"}
                        </MenuItem>

                        <MenuItem
                            onClick={() => { handlePublishToggle(); handleClose(); }}
                            disabled={isProcessing || isPendingReview}
                        >
                            {isPublic ? <Unpublished fontSize="small" sx={{ mr: 1 }} /> : <PublishedWithChanges fontSize="small" sx={{ mr: 1 }} />}
                            {isPublic
                                ? "Unpublish Extension"
                                : isPendingReview
                                    ? "Pending Review"
                                    : isRejected
                                        ? "Resubmit for Review"
                                        : "Submit for Review"}
                        </MenuItem>

                        <MenuItem
                            onClick={() => { handleUploadFile(); handleClose(); }}
                            disabled={isProcessing}
                        >
                            <UploadFile fontSize="small" sx={{ mr: 1 }} /> Upload File
                        </MenuItem>

                        <Divider />

                        <MenuItem
                            onClick={() => { handleDeleteClick(); handleClose(); }}
                            disabled={isProcessing}
                            sx={{ color: "error.main" }}
                        >
                            <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                        </MenuItem>
                    </>
                )}
            </Menu>


            <FileUploadPopup
                visible={showSidebar}
                onHide={() => setShowSidebar(false)}
                extensionId={extension.id}
                handleFileChangeFromSidebar={handleFileChangeFromSidebar}
                setSelectedDocumentIds={setSelectedDocumentIds}
            />
            <MuiDialog
                open={showDetailsDialog}
                onClose={() => setShowDetailsDialog(false)}
                fullWidth
                sx={{ "& .MuiDialog-paper": { borderRadius: "12px" } }}
            >
                {/* Header */}
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                        {/* Avatar Section */}
                        <Avatar
                            alt={extension.extensionName}
                            src={extension.imagePath}
                            sx={{ width: 56, height: 56 }} // Adjust the size of the avatar
                        />

                        {/* Text Section */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                            {/* Extension Name */}
                            <Typography
                                variant="h2"
                                fontWeight={700}
                                color="purple"
                            >
                                {extension?.extensionName ?
                                    extension.extensionName.charAt(0).toUpperCase() + extension.extensionName.slice(1).toLowerCase()
                                    : "Unknown Extension"}
                            </Typography>

                            {/* Author */}
                            {(extension?.userName || extension?.id === 'DONA') && (
                                <Typography
                                    color="text.secondary"
                                    sx={{ fontSize: "14px" }} // Set font size in pixels
                                >
                                    Created by <strong>{extension?.id === 'DONA' ? "Admin" : extension?.userName || "Unknown Author"}</strong>
                                </Typography>
                            )}
                        </Box>
                    </Box>



                    {/* Close Button */}
                    <IconButton
                        onClick={() => setShowDetailsDialog(false)}
                        sx={{
                            bgcolor: "grey.200",
                            "&:hover": { bgcolor: "grey.300" },
                            p: 0.5
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />

                {/* Content */}
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3, p: 3 }}>
                    {isPendingReview && (
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "#fff8e1", border: "1px solid #ffc107" }}>
                            <Typography variant="body2" sx={{ color: "#7a5800", fontWeight: 600 }}>
                                Pending review &mdash; an admin is reviewing this extension before it becomes public.
                            </Typography>
                        </Box>
                    )}
                    {isRejected && extension?.rejectionReason && (
                        <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "#fdecea", border: "1px solid #d32f2f" }}>
                            <Typography variant="body2" sx={{ color: "#7f1d1d", fontWeight: 600 }}>
                                Rejected by reviewer
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#7f1d1d", mt: 0.5 }}>
                                {extension.rejectionReason}
                            </Typography>
                        </Box>
                    )}
                    {[
                        { label: "Description", value: extension?.extensionDescription || "No description available." },
                        { label: "Instructions", value: extension?.extensionInstructions || "No instructions provided." }
                    ].map((item, index) => (
                        <Box key={index} sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                            <Box sx={{ minWidth: "120px" }}>
                                <Typography variant="h6" fontWeight={600} color="text.primary">
                                    {item.label}
                                </Typography>
                            </Box>
                            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                {item.value}
                            </Typography>
                        </Box>
                    ))}

                    {/* Tools Section */}
                    <Box sx={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                        <Box sx={{ minWidth: "120px" }}>
                            <Typography variant="h6" fontWeight={600} color="text.primary">
                                Tools
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {extension?.tools?.length ? (
                                extension.tools.map((tool, index) => (
                                    <Chip
                                        key={index}
                                        label={tool}
                                        size="small"
                                        sx={{
                                            bgcolor: "purple",
                                            color: "white",
                                            borderRadius: "4px",
                                            fontWeight: 500,
                                            "&:hover": { bgcolor: "purple.dark" }
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography variant="body1" color="text.secondary">
                                    No tools listed.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
            </MuiDialog>

            <Dialog
                header="Edit Extension"
                visible={showEditDialog}
                style={{ width: "700px" }}
                onHide={handleCloseEditDialog}
                modal
                draggable={false}
                resizable={false}
                className="extension-editor-dialog"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setTriggerCancel(true)}  // Trigger cancel event
                        />
                        <Button
                            label={isLoading ? "Updating..." : "Update"}
                            icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                            className="p-button-primary"
                            disabled={isLoading} // Optional: Disable button while loading
                            onClick={() => setTriggerCreate(true)}
                        />

                    </div>
                }
            > 
                <ExtensionCreation
                    existingExtension={extension}
                    onExtensionCreated={handleExtensionUpdated}
                    onExtensionUpdated={handleExtensionUpdated}
                    onCancel={handleCloseEditDialog}
                    triggerCreate={triggerCreate}
                    triggerCancel={triggerCancel}
                    resetTriggers={() => {
                        setTriggerCreate(false);
                        setTriggerCancel(false);
                    }}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                />
            </Dialog>

            <ExtensionPublishConfirmation
                visible={isPublishConfirmVisible}
                extension={extension}
                isAdmin={isAdmin}
                isProcessing={isProcessing}
                error={error}
                onConfirm={handleConfirmPublish}
                onCancel={() => setPublishConfirmVisible(false)}
            />

            <ExtensionUnpublishConfirmation
                visible={isUnpublishConfirmVisible}
                extension={extension}
                isProcessing={isProcessing}
                error={error}
                onConfirm={handleConfirmUnpublish}
                onCancel={() => setUnpublishConfirmVisible(false)}
            />

            <ExtensionDeletionConfirmation
                visible={isDeletionConfirmVisible}
                extension={extension}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                isProcessing={isProcessing}
                error={error}
            />
        </Box>
    );
};

ExtensionDialog.propTypes = {
    extension: PropTypes.object.isRequired,
    onStartChat: PropTypes.func,
    onUpdateExtension: PropTypes.func.isRequired,
    onRemoveExtension: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default ExtensionDialog;